class FormulaMatrix extends HTMLElement {
  #controller = null;
  #mediaQuery = null;
  #resizeObserver = null;
  #trunkCollapseHandler = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.#mediaQuery = window.matchMedia('(min-width: 950px)');

    const { signal } = this.#controller;

    this.#buildConnectorBranches();
    this.#positionConnectorBranches();
    this.#observeLayout(signal);
    this.#mediaQuery.addEventListener('change', () => this.#syncMode(), { signal });
    this.#bindCards(signal);
    this.#syncMode();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#controller?.abort();
    this.#controller = null;
    this.#mediaQuery = null;
  }

  #observeLayout(signal) {
    const body = this.querySelector('.formula-matrix__body');

    if (!body || typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#mediaQuery?.matches) return;
      this.#positionConnectorBranches();
      this.#restoreConnectorPath();
    });

    this.#resizeObserver.observe(body);
    window.addEventListener('load', () => this.#positionConnectorBranches(), { once: true, signal });
  }

  #getCards() {
    return [...this.querySelectorAll('[data-formula-matrix-card]')];
  }

  #getMiddleIndex(cardCount) {
    return Math.floor((cardCount - 1) / 2);
  }

  #buildConnectorBranches() {
    const track = this.querySelector('[data-formula-matrix-connector-track]');
    const cards = this.#getCards();

    if (!track || cards.length === 0) return;

    const middleIndex = this.#getMiddleIndex(cards.length);

    for (const branch of track.querySelectorAll('[data-connector-branch]')) {
      branch.remove();
    }

    for (const [index, card] of cards.entries()) {
      const branch = document.createElement('div');
      branch.className = 'formula-matrix__connector-branch';
      branch.dataset.connectorBranch = '';

      if (index === middleIndex) {
        const lineToImage = document.createElement('span');
        lineToImage.className = 'formula-matrix__connector-line formula-matrix__connector-line--to-image';
        branch.appendChild(lineToImage);
      }

      const dot = document.createElement('span');
      dot.className = 'formula-matrix__connector-dot';
      dot.dataset.connectorDot = '';

      const lineRight = document.createElement('span');
      lineRight.className = 'formula-matrix__connector-line formula-matrix__connector-line--right';

      branch.append(dot, lineRight);
      track.appendChild(branch);

      card.dataset.connectorIndex = String(index);
    }
  }

  #positionConnectorBranches() {
    const track = this.querySelector('[data-formula-matrix-connector-track]');
    const branches = [...this.querySelectorAll('[data-connector-branch]')];
    const cards = this.#getCards();

    if (!track || branches.length === 0 || branches.length !== cards.length) return;

    const trackRect = track.getBoundingClientRect();

    for (const [index, branch] of branches.entries()) {
      const card = cards[index];
      const cardRect = card.getBoundingClientRect();
      const centerY = cardRect.top + cardRect.height / 2 - trackRect.top;

      branch.style.top = `${centerY}px`;
    }
  }

  #getBranches() {
    return [...this.querySelectorAll('[data-connector-branch]')];
  }

  #getActiveConnectorIndex() {
    const value = this.dataset.activeConnector;

    if (value === undefined) return null;

    return Number.parseInt(value, 10);
  }

  #restoreConnectorPath() {
    const activeIndex = this.#getActiveConnectorIndex();

    if (activeIndex === null || Number.isNaN(activeIndex)) return;

    this.#updateConnectorPath(activeIndex);
  }

  #cancelTrunkCollapse(trunkSegment) {
    if (!trunkSegment || !this.#trunkCollapseHandler) return;

    trunkSegment.removeEventListener('transitionend', this.#trunkCollapseHandler);
    this.#trunkCollapseHandler = null;
  }

  #syncTrunkSegment(trunkSegment, { top, height, origin, scale = 1 }) {
    if (!trunkSegment) return;

    trunkSegment.style.setProperty('--fm-trunk-top', `${top}px`);
    trunkSegment.style.setProperty('--fm-trunk-height', `${Math.max(0, height)}px`);
    trunkSegment.style.setProperty('--fm-trunk-scale', String(scale));
    trunkSegment.style.setProperty('--fm-trunk-origin', origin);
  }

  #clearConnectorPath() {
    const branches = this.#getBranches();
    const trunkSegment = this.querySelector('[data-connector-trunk-segment]');

    for (const branch of branches) {
      branch.classList.remove(
        'formula-matrix__connector-branch--live',
        'formula-matrix__connector-branch--live-image',
        'formula-matrix__connector-branch--live-junction',
      );
    }

    this.#cancelTrunkCollapse(trunkSegment);
    trunkSegment?.classList.remove('formula-matrix__connector-trunk-segment--live');

    if (trunkSegment) {
      trunkSegment.style.setProperty('--fm-trunk-top', '0px');
      trunkSegment.style.setProperty('--fm-trunk-height', '0px');
      trunkSegment.style.setProperty('--fm-trunk-scale', '0');
      trunkSegment.style.removeProperty('--fm-trunk-origin');
    }

    delete this.dataset.activeConnector;
  }

  #clearActiveState() {
    for (const card of this.#getCards()) {
      card.classList.remove('formula-matrix-card--active');
    }

    this.#clearConnectorPath();
  }

  #bindCards(signal) {
    for (const card of this.#getCards()) {
      const summary = card.querySelector('.formula-matrix-card__summary');

      summary?.addEventListener(
        'click',
        (event) => {
          if (!this.#mediaQuery?.matches) return;
          event.preventDefault();
        },
        { signal },
      );

      summary?.addEventListener(
        'keydown',
        (event) => {
          if (!this.#mediaQuery?.matches) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
        },
        { signal },
      );

      card.addEventListener(
        'toggle',
        () => {
          if (this.#mediaQuery?.matches) return;
          this.#handleMobileToggle(card);
        },
        { signal },
      );

      card.addEventListener(
        'mouseenter',
        () => {
          if (!this.#mediaQuery?.matches) return;
          this.#setActiveCard(card);
        },
        { signal },
      );

      card.addEventListener(
        'mouseleave',
        (event) => {
          if (!this.#mediaQuery?.matches) return;

          const nextCard = event.relatedTarget?.closest('[data-formula-matrix-card]');

          if (nextCard && this.contains(nextCard)) return;

          this.#clearActiveState();
        },
        { signal },
      );

      card.addEventListener(
        'focusin',
        () => {
          if (!this.#mediaQuery?.matches) return;
          this.#setActiveCard(card);
        },
        { signal },
      );

      card.addEventListener(
        'focusout',
        (event) => {
          if (!this.#mediaQuery?.matches) return;

          const nextCard = event.relatedTarget?.closest('[data-formula-matrix-card]');

          if (nextCard && this.contains(nextCard)) return;

          this.#clearActiveState();
        },
        { signal },
      );
    }

    this.addEventListener(
      'mouseleave',
      () => {
        if (!this.#mediaQuery?.matches) return;
        this.#clearActiveState();
      },
      { signal },
    );
  }

  #syncMode() {
    const cards = this.#getCards();

    if (this.#mediaQuery?.matches) {
      for (const card of cards) {
        card.open = true;
      }

      this.#positionConnectorBranches();
      this.#clearActiveState();
      return;
    }

    delete this.dataset.activeConnector;

    const openCard = cards.find((card) => card.open) || cards[0];

    for (const card of cards) {
      card.open = card === openCard;
    }
  }

  #handleMobileToggle(activeCard) {
    if (!activeCard.open) return;

    for (const card of this.#getCards()) {
      if (card !== activeCard) {
        card.open = false;
      }
    }
  }

  #updateConnectorPath(activeIndex) {
    const cards = this.#getCards();
    const branches = this.#getBranches();
    const middleIndex = this.#getMiddleIndex(cards.length);
    const trunkSegment = this.querySelector('[data-connector-trunk-segment]');
    const middleBranch = branches[middleIndex];

    for (const [index, branch] of branches.entries()) {
      branch.classList.toggle('formula-matrix__connector-branch--live', index === activeIndex);
      branch.classList.toggle('formula-matrix__connector-branch--live-image', index === middleIndex);
      branch.classList.toggle(
        'formula-matrix__connector-branch--live-junction',
        index === middleIndex && activeIndex !== middleIndex,
      );
    }

    if (!trunkSegment || !middleBranch) return;

    const middleY = Number.parseFloat(middleBranch.style.top) || 0;
    const activeY = Number.parseFloat(branches[activeIndex]?.style.top) || 0;

    this.#cancelTrunkCollapse(trunkSegment);

    if (activeIndex !== middleIndex) {
      const height = Math.abs(middleY - activeY);
      const top = activeY > middleY ? middleY : activeY;

      this.#syncTrunkSegment(trunkSegment, {
        top,
        height,
        origin: activeY > middleY ? 'top' : 'bottom',
        scale: 1,
      });
      trunkSegment.classList.add('formula-matrix__connector-trunk-segment--live');
      return;
    }

    if (!trunkSegment.classList.contains('formula-matrix__connector-trunk-segment--live')) {
      this.#syncTrunkSegment(trunkSegment, { top: 0, height: 0, origin: 'top', scale: 0 });
      return;
    }

    const currentTop = Number.parseFloat(trunkSegment.style.getPropertyValue('--fm-trunk-top')) || 0;
    const currentHeight = Number.parseFloat(trunkSegment.style.getPropertyValue('--fm-trunk-height')) || 0;
    const currentOrigin = trunkSegment.style.getPropertyValue('--fm-trunk-origin') || 'top';

    this.#syncTrunkSegment(trunkSegment, {
      top: currentTop,
      height: currentHeight,
      origin: currentOrigin,
      scale: 0,
    });

    this.#trunkCollapseHandler = (event) => {
      if (event.target !== trunkSegment || event.propertyName !== 'transform') return;

      this.#cancelTrunkCollapse(trunkSegment);
      trunkSegment.classList.remove('formula-matrix__connector-trunk-segment--live');
      trunkSegment.style.setProperty('--fm-trunk-top', '0px');
      trunkSegment.style.setProperty('--fm-trunk-height', '0px');
      trunkSegment.style.removeProperty('--fm-trunk-origin');
    };

    trunkSegment.addEventListener('transitionend', this.#trunkCollapseHandler);
  }

  #setActiveCard(activeCard) {
    const cards = this.#getCards();
    const index = cards.indexOf(activeCard);

    this.dataset.activeConnector = String(index);

    for (const card of cards) {
      card.classList.toggle('formula-matrix-card--active', card === activeCard);
    }

    this.#updateConnectorPath(index);
  }
}

if (!customElements.get('formula-matrix')) {
  customElements.define('formula-matrix', FormulaMatrix);
}
